import React, { useState, useEffect, useCallback } from 'react';

import { useWindowFocus } from '@src/hooks/useWindowFocus';

/* Api */
import { useMutationCreateCreator } from '@src/queries/influencers/useMutationCreateCreator';
import { useQueryProductsAvailableCountries } from '@src/queries/products/useQueryProducts';

/* Styling */
import { stylesheet } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

/* Misc */
import { EPopup } from '@src/types/common';
import { setPopup } from '@src/state/popupAtoms';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { useQueryUserConfig } from '../../../../../queries/users/useQueryUserConfig';

/* Components */
import { PopupOverlay } from '@src/components/PopupOverlay';
import { Input } from 'components/Input';
import { ActionButton } from '@src/components/ActionButton';
import { SectionProgressLoader } from '@src/components/SectionProgressLoader';

/* Types */
import { Services } from '@src/types/Services';

interface Props {
  services: Services;
}

export function AddInfluencer(props: Props) {
  const { data: userConfig } = useQueryUserConfig();
  const user = userConfig?.user ?? null;
  const catalogCountries = user?.catalogCountries;
  const focus = useWindowFocus();
  const [mounted, setMounted] = useState(false);
  const [influencerData, setInfluencerData] = useState<{ name: string, countries?: string[] }>({ name: '', countries: [] });

  const { mutateAsync: mutateInfluencer, isPending: saving } = useMutationCreateCreator();

  const handleLocalizationChange = (event: React.ChangeEvent<HTMLInputElement>, name: string) => {
    if (event.target.checked) {
      setInfluencerData({
        ...influencerData,
        countries: [...influencerData.countries || [], name],
      });
    } else {
      setInfluencerData({
        ...influencerData,
        countries: influencerData.countries ? [...influencerData.countries?.filter((country) => country !== name)] : [],
      });
    }
  };

  const onSaveClick = async () => {
    const countriesToExclude = (catalogCountries ?? []).filter((country) => !influencerData.countries?.includes(country));
    await mutateInfluencer({
      username: influencerData.name,
      countries: influencerData.countries,
      countriesToExclude: countriesToExclude,
      isInfluencer: true,
      isInfluencerDisabled: false,
      isBlacklisted: false,
      upsert: true,
    });
    close();
  };

  const refresh = (): void | NodeJS.Timeout => {
    if (!mounted) {
      return;
    }
    const loop = () => setTimeout(() => refresh(), 3000);
    if (!focus) {
      return loop();
    }
  };

  const close = useCallback(() => {
    setPopup(EPopup.addInfluencer, false);
  }, []);

  useEffect(() => {
    setMounted(true);
    refresh();
    return () => {
      setMounted(false);
    };
  }, []);

  const disableSubmit = influencerData.name.length < 1 || influencerData.name.length > 30;

  if (!user) {
    return null; // TODO: loading
  }
  return (
    <>
      <PopupOverlay show={true} onClose={close} title={lang.title} width="min(500px, 92vw)" zIndex={3000}>

        <Input
          onChange={({ target: { value } }) =>
            setInfluencerData({ ...influencerData, name: value })
          }
          value={influencerData.name}
          placeholder={lang.name}
        />

        {catalogCountries?.length ? (
          <div className={sheet.text}>
            {lang.localizations}

            <FormGroup row={true}>
              {catalogCountries.map((country, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      sx={{
                        color: ADALONG_COLORS.LIGHT_GRAY,
                        '&.Mui-checked': {
                          color: ADALONG_COLORS.LIGHT_GRAY,
                        },
                      }}
                      onChange={(e) => handleLocalizationChange(e, country)}
                    />
                  }
                  label={country}
                />
              ))}
            </FormGroup>
          </div>
        ) : undefined}

        <div className={sheet.text}>{lang.desc}</div>

        <div className={sheet.buttonContainer}>
          <ActionButton
            variant='blue'
            onAction={onSaveClick}
            disabled={disableSubmit}
            data-size='medium'
          >
            {lang.save.toUpperCase()}
          </ActionButton>
        </div>

        {saving ? <SectionProgressLoader indeterminate opacity={0.6} /> : null}
      </PopupOverlay>
    </>
  );
}

const lang = LocalizedStrings({
  en: {
    title: 'Add an Influencer',
    name: 'Enter an instagram name',
    desc:
      '⚠️ I am aware that by adding this influencer to my whitelist, I do have a written agreement about collecting and using their content for marketing purposes. See more on https://www.adalong.com/yesadalong/.',
    error: 'An error occured, please try later or contact us',
    save: 'Add to my whitelist',
    localizations: 'For which market(s) is this influencer contracted?',
  },
});

const sheet = stylesheet({
  buttonContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  text: {
    margin: '20px 0',
  },
});
export default AddInfluencer;
